import React, { useCallback, useRef } from "react";
import { useIntl } from "react-intl";
import Popover from "../../../../../../library/UI/Popover";
import { resolutionToLabel } from "../../constants";
import ChartTypeTiles from "./ChartTypeTiles";
import ResolutionGrid from "./ResolutionGrid";
import ChartSettings from "./ChartSettings";
import ChartTypeIcon from "./ChartTypeIcon";
import { isLineChart } from "../../settings/effectiveChartSettings";
import useFitChartMenu from "../../hooks/useFitChartMenu";
import "./ChartTypeMenu.sass";

const TRIGGER_ICON_SIZE = 18;

/**
 * Chart type and timeframe selector button + popover.
 * The button icon shows the current chart type, the badge the current resolution.
 * The popover contains ChartTypeTiles, ResolutionGrid and ChartSettings.
 * Line has a fixed resolution, no timer and no candle colours, so those controls are hidden for it.
 *
 * @param {object}   props
 * @param {number}   props.chartType       - current SeriesType value
 * @param {string}   props.resolution      - current TV resolution string
 * @param {Function} props.onChartType     - (type: number) => void
 * @param {Function} props.onResolution    - (resolution: string) => void
 * @param {object}   props.chartSettingsApi - { settings, updateSettings }
 * @param {boolean}  props.isOpen
 * @param {Function} props.onToggle
 * @param {Function} props.onClose
 * @param {Function} props.onOpenCandleColors - () => void
 */
const ChartTypeMenu = ({
  chartType,
  resolution,
  onChartType,
  onResolution,
  chartSettingsApi,
  isOpen,
  onToggle,
  onClose,
  onOpenCandleColors,
}) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const menuRef = useRef(null);
  const menuStyle = useFitChartMenu(menuRef, isOpen);

  const activeClass = isOpen ? " chart-toolbar__btn--active" : "";

  const { settings, updateSettings } = chartSettingsApi;
  const isLine = isLineChart(chartType);

  const handleTimerChange = useCallback(
    () => updateSettings({ timerEnabled: !settings.timerEnabled }),
    [updateSettings, settings.timerEnabled],
  );

  const handleAutoscrollChange = useCallback(
    () => updateSettings({ autoscrollEnabled: !settings.autoscrollEnabled }),
    [updateSettings, settings.autoscrollEnabled],
  );

  const handleGridSnapChange = useCallback(
    () => updateSettings({ gridSnapEnabled: !settings.gridSnapEnabled }),
    [updateSettings, settings.gridSnapEnabled],
  );

  const trigger = (
    <button
      type="button"
      className={`chart-toolbar__btn${activeClass}`}
      aria-label={t("futures.chart.toolbar.chart_type")}
      title={t("futures.chart.toolbar.chart_type")}
      aria-expanded={isOpen}
      onClick={onToggle}
    >
      <span className="chart-toolbar__btn-icon">
        <ChartTypeIcon type={chartType} size={TRIGGER_ICON_SIZE} />
      </span>
      <span className="chart-toolbar__badge" aria-hidden="true">
        {resolutionToLabel(resolution)}
      </span>
    </button>
  );

  return (
    <Popover isOpen={isOpen} onClose={onClose} trigger={trigger}>
      <div className="chart-type-menu" ref={menuRef} style={menuStyle}>
        <section className="chart-type-menu__section">
          <p className="chart-type-menu__section-title chart-type-menu__section-title--small">
            {t("futures.chart.type_menu.chart_types")}
          </p>
          <ChartTypeTiles activeType={chartType} onSelect={onChartType} />
        </section>

        {!isLine && (
          <section className="chart-type-menu__section">
            <p className="chart-type-menu__section-title">
              {t("futures.chart.type_menu.time_frames")}
            </p>
            <ResolutionGrid activeResolution={resolution} onSelect={onResolution} />
          </section>
        )}

        <section className="chart-type-menu__section">
          <div className="chart-type-menu__section-title-row">
            <p className="chart-type-menu__section-title">
              {t("futures.chart.type_menu.settings")}
            </p>
            {!isLine && (
              <button
                type="button"
                className="chart-type-menu__candle-colors-link"
                onClick={onOpenCandleColors}
              >
                {t("futures.chart.type_menu.custom_candle_colors")}
              </button>
            )}
          </div>
          <ChartSettings
            isTimerAvailable={!isLine}
            timerEnabled={settings.timerEnabled}
            autoscrollEnabled={settings.autoscrollEnabled}
            gridSnapEnabled={settings.gridSnapEnabled}
            onTimerChange={handleTimerChange}
            onAutoscrollChange={handleAutoscrollChange}
            onGridSnapChange={handleGridSnapChange}
          />
        </section>
      </div>
    </Popover>
  );
};

export default ChartTypeMenu;
